import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Link,
  Radio,
  RadioGroup,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../supabaseClient";
import {
  ANIMALS_WHERE_OPTIONS,
  ANIMALS_WHERE_QUESTION,
  AnimalsWhere,
  Answers,
  BranchKey,
  CONTINUATION_QUESTION,
  FLOWCHART_URL,
  FORM_BY_KEY,
  formsFor,
  needsPreApproval,
  OCSEF_IF_IN_DOUBT,
  OCSEF_SOURCE,
  Verdict,
} from "../../lib/fairForms";

/**
 * Step 6: which OCSEF forms does this project need?
 *
 * forms-wizard reads the student's Steps 1–5 writing and answers OCSEF's
 * branch questions with the sentence that led to each answer. The student
 * confirms or corrects every branch — the AI proposes, the student decides —
 * and "Apply to my checklist" writes the answers and the resulting forms to
 * the Requirements page, which is where the paperwork is then tracked.
 *
 * Unsure counts as yes until the student says otherwise, which is OCSEF's own
 * rule ("if in doubt, complete the form(s) and submit for pre-approval").
 */

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL ?? "";
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY ?? "";

interface Finding {
  key: BranchKey;
  question: string;
  examples: string[];
  verdict: Verdict;
  evidence: string;
  why: string;
}

interface WizardResult {
  ran_at: string;
  findings: Finding[];
  animals_where: AnimalsWhere;
  forms: string[];
  pre_approval: boolean;
  fair: string | null;
  deadlines: {
    pre_approval_date?: string | null;
    registration_date?: string | null;
    submission_date?: string | null;
  };
  applied_at?: string;
}

const VERDICT_LABEL: Record<Verdict, string> = {
  yes: "Yes",
  unsure: "Not sure",
  no: "No",
};
const VERDICT_COLOR: Record<Verdict, "warning" | "success" | "default"> = {
  yes: "warning",
  unsure: "warning",
  no: "default",
};

const fmtDate = (d?: string | null) => {
  if (!d) return null;
  const [y, m, day] = d.split("-").map(Number);
  return new Date(y, m - 1, day).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};
const daysUntil = (d?: string | null) => {
  if (!d) return null;
  const [y, m, day] = d.split("-").map(Number);
  return Math.ceil(
    (new Date(y, m - 1, day).getTime() - Date.now()) / 86400000,
  );
};

export default function FormsWizard({
  projectId,
  studentId,
  editable,
}: {
  projectId: string;
  studentId: string;
  editable: boolean;
}) {
  const navigate = useNavigate();
  const [result, setResult] = useState<WizardResult | null>(null);
  const [answers, setAnswers] = useState<Answers>({});
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [appliedAt, setAppliedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Last run, so the findings survive a reload.
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .from("project_requirement")
        .select("wizard, triage")
        .eq("project_id", projectId)
        .maybeSingle();
      if (data?.wizard) {
        const w = data.wizard as WizardResult;
        setResult(w);
        setAppliedAt(w.applied_at ?? null);
        setAnswers(
          (data.triage as Answers) ?? answersFromResult(w),
        );
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  function answersFromResult(w: WizardResult): Answers {
    const a: Answers = { animals_where: w.animals_where };
    for (const f of w.findings) a[f.key] = f.verdict !== "no";
    return a;
  }

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(`${SUPABASE_URL}/functions/v1/forms-wizard`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session?.access_token ?? ""}`,
        },
        body: JSON.stringify({}),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data?.ok === false)
        throw new Error(data?.error ?? `Request failed (${res.status})`);
      setResult(data as WizardResult);
      setAnswers({
        ...answersFromResult(data as WizardResult),
        continuation: answers.continuation,
      });
      setAppliedAt(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  async function apply() {
    if (!result) return;
    setApplying(true);
    setError(null);
    try {
      const keys = formsFor(answers);
      const { data: row } = await supabase
        .from("project_requirement")
        .select("forms")
        .eq("project_id", projectId)
        .maybeSingle();
      const forms: Record<string, { needed?: boolean; status?: string }> = {
        ...((row?.forms as Record<string, any>) ?? {}),
      };
      // Form 1B is on every project; the rest come from the answers.
      for (const k of ["1B", ...keys]) {
        forms[k] = { ...forms[k], needed: true };
      }
      const now = new Date().toISOString();
      const { error: e } = await supabase.from("project_requirement").upsert(
        {
          project_id: projectId,
          student_id: studentId,
          triage: answers,
          forms,
          wizard: { ...result, applied_at: now },
          updated_at: now,
        },
        { onConflict: "project_id" },
      );
      if (e) throw new Error(e.message);
      setAppliedAt(now);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApplying(false);
    }
  }

  const setAnswer = (key: BranchKey, value: boolean) =>
    setAnswers((a) => ({ ...a, [key]: value }));

  const keys = formsFor(answers);
  const preApproval = needsPreApproval(answers);
  const preDate = result?.deadlines?.pre_approval_date;
  const preDays = daysUntil(preDate);

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5 }}>
        Which OCSEF forms does your project need?
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
        This reads what you wrote in Steps 1–5 and answers the questions on
        OCSEF's pre-approval flowchart, showing the words in your own project
        that led to each answer. You confirm each one — then it fills in your{" "}
        <Link component="button" onClick={() => navigate("/requirements")} sx={{ verticalAlign: "baseline" }}>
          Requirements
        </Link>{" "}
        checklist, where you track the paperwork.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", mb: 2 }}>
        <Button variant="contained" onClick={run} disabled={!editable || running}>
          {running ? (
            <>
              <CircularProgress size={16} color="inherit" sx={{ mr: 1 }} />
              Reading your project…
            </>
          ) : result ? (
            "Check again"
          ) : (
            "Check which forms I need"
          )}
        </Button>
        {result && (
          <Typography variant="caption" color="text.secondary">
            Checked {new Date(result.ran_at).toLocaleString()}
          </Typography>
        )}
      </Box>

      {result && (
        <>
          {result.findings.map((f) => {
            const answered = answers[f.key] ?? f.verdict !== "no";
            return (
              <Card key={f.key} variant="outlined" sx={{ mb: 1.5 }}>
                <CardContent>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap", mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, flex: 1, minWidth: 220 }}>
                      {f.question}
                    </Typography>
                    <Chip size="small" color={VERDICT_COLOR[f.verdict]} label={`AI: ${VERDICT_LABEL[f.verdict]}`} />
                  </Box>
                  {f.why && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: f.evidence ? 0.5 : 1 }}>
                      {f.why}
                    </Typography>
                  )}
                  {f.evidence && (
                    <Typography
                      variant="body2"
                      sx={{ mb: 1, pl: 1.5, borderLeft: "3px solid", borderColor: "divider", fontStyle: "italic" }}
                    >
                      “{f.evidence}”
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                    OCSEF counts: {f.examples.join("; ")}
                  </Typography>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={answered ? "yes" : "no"}
                    disabled={!editable}
                    onChange={(_, v) => v && setAnswer(f.key, v === "yes")}
                  >
                    <ToggleButton value="yes">Yes, my project does</ToggleButton>
                    <ToggleButton value="no">No, it doesn't</ToggleButton>
                  </ToggleButtonGroup>

                  {f.key === "animals" && answered && (
                    <Box sx={{ mt: 1.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {ANIMALS_WHERE_QUESTION}
                      </Typography>
                      <RadioGroup
                        value={answers.animals_where ?? "school_home_field"}
                        onChange={(e) =>
                          setAnswers((a) => ({ ...a, animals_where: e.target.value as AnimalsWhere }))
                        }
                      >
                        {ANIMALS_WHERE_OPTIONS.map((o) => (
                          <FormControlLabel
                            key={o.value}
                            value={o.value}
                            disabled={!editable}
                            control={<Radio size="small" />}
                            label={<Typography variant="body2">{o.label}</Typography>}
                          />
                        ))}
                      </RadioGroup>
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}

          <Card variant="outlined" sx={{ mb: 2 }}>
            <CardContent>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!answers.continuation}
                    disabled={!editable}
                    onChange={(e) => setAnswers((a) => ({ ...a, continuation: e.target.checked }))}
                  />
                }
                label={<Typography variant="body2">{CONTINUATION_QUESTION}</Typography>}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                Not on OCSEF's flowchart — this one only you can answer. It adds ISEF Form 7.
              </Typography>
            </CardContent>
          </Card>

          <Alert severity={preApproval ? "warning" : "success"} sx={{ mb: 2 }}>
            {preApproval ? (
              <>
                <strong>Your project needs SRC pre-approval.</strong> Get these forms approved{" "}
                <strong>before you start experimenting</strong>
                {preDate && (
                  <>
                    , and register by <strong>{fmtDate(preDate)}</strong>
                    {typeof preDays === "number" && preDays >= 0 ? ` (${preDays} days away)` : ""}
                  </>
                )}
                . {OCSEF_IF_IN_DOUBT}
              </>
            ) : (
              <>
                <strong>No pre-approval needed</strong> based on your answers
                {result.deadlines?.registration_date && (
                  <>
                    . Registration runs to <strong>{fmtDate(result.deadlines.registration_date)}</strong>
                  </>
                )}
                {result.deadlines?.submission_date && (
                  <>, and projects are submitted by <strong>{fmtDate(result.deadlines.submission_date)}</strong></>
                )}
                . {OCSEF_IF_IN_DOUBT}
              </>
            )}
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            Forms for your project
          </Typography>
          <Box sx={{ mb: 2 }}>
            {["1B", ...keys].map((k) => {
              const form = FORM_BY_KEY[k];
              if (!form) return null;
              return (
                <Typography key={k} variant="body2" sx={{ mb: 0.5 }}>
                  • <strong>{form.code} · {form.title}</strong> — {form.when}
                </Typography>
              );
            })}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
            <Button variant="contained" onClick={apply} disabled={!editable || applying}>
              {applying ? "Saving…" : "Apply to my checklist"}
            </Button>
            <Button onClick={() => navigate("/requirements")}>Open Requirements</Button>
            {appliedAt && (
              <Typography variant="caption" color="success.main">
                Added to your Requirements checklist {new Date(appliedAt).toLocaleString()}
              </Typography>
            )}
          </Box>
        </>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 3, lineHeight: 1.6 }}>
        {OCSEF_SOURCE}{" "}
        <Link href={FLOWCHART_URL} target="_blank" rel="noopener noreferrer">
          Open OCSEF's flowchart
        </Link>
        .
      </Typography>
    </Box>
  );
}
